const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const normalize = (value, min, max) => {
	if (max === min) return 0;
	return clamp((value - min) / (max - min), 0, 1);
};

const STYLE_BASES = {
	swarm: {
		swarm: 1.18,
		speed: 1.12,
		armor: 0.94,
		support: 0.9,
		boss: 0.96,
	},
	transit: {
		swarm: 1.12,
		speed: 1.02,
		armor: 1.02,
		support: 0.94,
		boss: 1.04,
	},
	folded: {
		swarm: 0.95,
		speed: 1,
		armor: 1.08,
		support: 1.14,
		boss: 1.04,
	},
	maze: {
		swarm: 1,
		speed: 1.04,
		armor: 1,
		support: 1.04,
		boss: 1.02,
	},
	fortress: {
		swarm: 0.92,
		speed: 0.94,
		armor: 1.1,
		support: 1.08,
		boss: 1.12,
	},
};

const STYLE_TUNING = {
	swarm: {
		startingGold: -8,
		enemyHp: 0.03,
		enemySpeed: 0.02,
		enemyReward: 0,
		bonusGoldScale: 0,
		cacheGoldScale: 0,
		pressure: 0.08,
		campaignBudget: 0.07,
		survivalBudget: 0.09,
		bodyCap: 0,
	},
	transit: {
		startingGold: 60,
		enemyHp: -0.09,
		enemySpeed: -0.09,
		enemyReward: 0.05,
		bonusGoldScale: 0.05,
		cacheGoldScale: 0.05,
		pressure: -0.18,
		campaignBudget: -0.17,
		survivalBudget: -0.21,
		bodyCap: -2,
	},
	folded: {
		startingGold: -18,
		enemyHp: 0.08,
		enemySpeed: 0.03,
		enemyReward: -0.03,
		bonusGoldScale: -0.03,
		cacheGoldScale: -0.03,
		pressure: 0.13,
		campaignBudget: 0.16,
		survivalBudget: 0.18,
		bodyCap: 0,
	},
	maze: {
		startingGold: -8,
		enemyHp: 0.045,
		enemySpeed: 0.025,
		enemyReward: -0.015,
		bonusGoldScale: -0.015,
		cacheGoldScale: -0.015,
		pressure: 0.095,
		campaignBudget: 0.12,
		survivalBudget: 0.1,
		bodyCap: 0,
	},
	fortress: {
		startingGold: -20,
		enemyHp: 0.11,
		enemySpeed: 0.045,
		enemyReward: -0.055,
		bonusGoldScale: -0.055,
		cacheGoldScale: -0.055,
		pressure: 0.23,
		campaignBudget: 0.27,
		survivalBudget: 0.3,
		bodyCap: 2,
	},
};

const DEFAULT_METRICS = {
	difficulty: 3.2,
	pathLength: 82,
	turnCount: 12,
	foldContacts: 10,
	edgeRatio: 0.28,
	centerPull: 5.4,
	coverage: 110,
	averageSegment: 4.6,
};

const FALLBACK_STYLE_ORDER = ["swarm", "transit", "folded", "maze", "fortress"];

const GLOBAL_DIFFICULTY_SHIFT = {
	startingGold: 0.88,
	enemyHp: 1.14,
	enemySpeed: 1.055,
	enemyReward: 0.95,
	bonusGoldScale: 0.84,
	cacheGoldScale: 0.81,
	pressure: 1.15,
	waveBudget: 1.21,
	lateBudget: 1.28,
	bodyCap: 1.11,
};

const resolveContext = (context = {}) => {
	if (typeof context === "number") {
		return {
			mapIdx: context,
			style: FALLBACK_STYLE_ORDER[context] || FALLBACK_STYLE_ORDER[FALLBACK_STYLE_ORDER.length - 1],
			metrics: DEFAULT_METRICS,
		};
	}

	if (context?.mapDef) {
		return {
			mapIdx: context.mapIdx || 0,
			style: context.mapDef.style || FALLBACK_STYLE_ORDER[context.mapIdx || 0] || "swarm",
			metrics: context.mapDef.metrics || DEFAULT_METRICS,
		};
	}

	if (context?.metrics) {
		return {
			mapIdx: context.mapIdx || 0,
			style: context.style || FALLBACK_STYLE_ORDER[context.mapIdx || 0] || "swarm",
			metrics: context.metrics,
		};
	}

	return {
		mapIdx: context?.mapIdx || 0,
		style: context?.style || FALLBACK_STYLE_ORDER[context?.mapIdx || 0] || "swarm",
		metrics: DEFAULT_METRICS,
	};
};

export const deriveMapBalance = (context = {}) => {
	const { mapIdx = 0, style, metrics: sourceMetrics } = resolveContext(context);
	const metrics = { ...DEFAULT_METRICS, ...(sourceMetrics || {}) };
	const styleBase = STYLE_BASES[style] || STYLE_BASES.swarm;
	const styleTune = STYLE_TUNING[style] || STYLE_TUNING.swarm;

	const hardness = normalize(metrics.difficulty, 1, 5);
	const ease = 1 - hardness;
	const longLane = normalize(metrics.pathLength, 48, 150);
	const turnDensity = normalize(metrics.turnCount, 6, 28);
	const overlap = normalize(metrics.foldContacts, 0, 28);
	const edgeBias = normalize(metrics.edgeRatio, 0.08, 0.68);
	const centerEase = 1 - normalize(metrics.centerPull, 2.5, 8.6);
	const coverage = normalize(metrics.coverage, 72, 152);
	const straightness = normalize(metrics.averageSegment, 2.2, 9.8);
	const buildEase = clamp(longLane * 0.22 + overlap * 0.18 + centerEase * 0.2 + coverage * 0.22 + straightness * 0.18, 0, 1);
	const scarcity = clamp(edgeBias * 0.5 + (1 - coverage) * 0.35 + (1 - overlap) * 0.15, 0, 1);

	const startingGold = Math.round((168 + hardness * 62 + scarcity * 18 - buildEase * 28 + styleTune.startingGold) * GLOBAL_DIFFICULTY_SHIFT.startingGold);
	const enemyHp = Number(((1.03 + hardness * 0.17 + scarcity * 0.05 - buildEase * 0.03 + styleTune.enemyHp) * GLOBAL_DIFFICULTY_SHIFT.enemyHp).toFixed(2));
	const enemySpeed = Number(((0.97 + straightness * 0.07 + longLane * 0.05 + scarcity * 0.04 + styleTune.enemySpeed) * GLOBAL_DIFFICULTY_SHIFT.enemySpeed).toFixed(2));
	const enemyReward = Number(((0.45 + hardness * 0.12 + scarcity * 0.03 - buildEase * 0.04 + styleTune.enemyReward) * GLOBAL_DIFFICULTY_SHIFT.enemyReward).toFixed(2));
	const bonusGoldScale = Number(((0.46 + hardness * 0.1 + scarcity * 0.02 - buildEase * 0.03 + styleTune.bonusGoldScale) * GLOBAL_DIFFICULTY_SHIFT.bonusGoldScale).toFixed(2));
	const cacheGoldScale = Number(((0.4 + hardness * 0.08 + scarcity * 0.02 - buildEase * 0.04 + styleTune.cacheGoldScale) * GLOBAL_DIFFICULTY_SHIFT.cacheGoldScale).toFixed(2));
	const pressure = Number(((1.24 + hardness * 0.14 + scarcity * 0.06 - buildEase * 0.08 + styleTune.pressure) * GLOBAL_DIFFICULTY_SHIFT.pressure).toFixed(2));
	const campaignBudget = Number(((1.2 + hardness * 0.2 + scarcity * 0.04 - buildEase * 0.1 + styleTune.campaignBudget) * GLOBAL_DIFFICULTY_SHIFT.waveBudget).toFixed(2));
	const campaignLateBudget = Number(((campaignBudget + 0.12 + hardness * 0.06) * GLOBAL_DIFFICULTY_SHIFT.lateBudget).toFixed(2));
	const survivalBudget = Number(((1.22 + hardness * 0.18 + scarcity * 0.04 - buildEase * 0.08 + styleTune.survivalBudget) * GLOBAL_DIFFICULTY_SHIFT.waveBudget).toFixed(2));

	const bodyBias = clamp(0.46 + ease * 0.28 + buildEase * 0.14 - scarcity * 0.12, 0.3, 0.82);
	const campaignBodyCapBase = Math.round((15 + bodyBias * 4 + styleTune.bodyCap) * GLOBAL_DIFFICULTY_SHIFT.bodyCap);
	const campaignBodyCap = [
		campaignBodyCapBase,
		campaignBodyCapBase + 6 + Math.round(buildEase * 2),
		campaignBodyCapBase + 10 + Math.round(buildEase * 3),
		campaignBodyCapBase + 9 + Math.round(buildEase * 3),
	];
	const survivalBodyCapBase = Math.round((17 + bodyBias * 4 + styleTune.bodyCap) * GLOBAL_DIFFICULTY_SHIFT.bodyCap);

	return {
		name: context?.mapDef?.name || `Sector ${mapIdx + 1}`,
		style,
		difficulty: metrics.difficulty,
		startingGold,
		enemyHp,
		enemySpeed,
		enemyReward,
		bonusGoldScale,
		cacheGoldScale,
		pressure,
		swarm: Number(clamp(styleBase.swarm + ease * 0.08 + longLane * 0.06 - overlap * 0.08, 0.84, 1.28).toFixed(2)),
		speed: Number(clamp(styleBase.speed + straightness * 0.06 + longLane * 0.06 - hardness * 0.02, 0.88, 1.24).toFixed(2)),
		armor: Number(clamp(styleBase.armor + overlap * 0.16 + coverage * 0.04, 0.88, 1.18).toFixed(2)),
		support: Number(clamp(styleBase.support + overlap * 0.14 + centerEase * 0.06, 0.86, 1.22).toFixed(2)),
		boss: Number(clamp(styleBase.boss + edgeBias * 0.08 + scarcity * 0.05, 0.9, 1.18).toFixed(2)),
		campaignBudget,
		campaignLateBudget,
		survivalBudget,
		campaignBodyCap,
		survivalBodyCapBase,
		meta: {
			mapIdx,
			buildEase: Number(buildEase.toFixed(2)),
			scarcity: Number(scarcity.toFixed(2)),
			longLane: Number(longLane.toFixed(2)),
			turnDensity: Number(turnDensity.toFixed(2)),
			overlap: Number(overlap.toFixed(2)),
			edgeBias: Number(edgeBias.toFixed(2)),
			centerEase: Number(centerEase.toFixed(2)),
			coverage: Number(coverage.toFixed(2)),
			straightness: Number(straightness.toFixed(2)),
		},
	};
};

export const getMapBalance = (context = {}) => deriveMapBalance(context);

export const getFallbackMapCount = () => FALLBACK_STYLE_ORDER.length;
